import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Activity, TrendingUp, Users, Zap } from 'lucide-react'
import { useDashboard } from '@/hooks/useDashboard'

interface MetricCardProps {
  title: string
  value: string
  change: string
  trend: 'up' | 'down'
  icon: React.ReactNode
}

const MetricCard = ({ title, value, change, trend, icon }: MetricCardProps) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium">{title}</CardTitle>
      {icon}
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      <p className={`text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
        {change} from last period
      </p>
    </CardContent>
  </Card>
)

export const MetricsDashboardPage = () => {
  const { data, isLoading, error } = useDashboard()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Metrics Dashboard</h1>
          <p className="text-muted-foreground">
            System-wide performance and usage metrics
          </p>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading metrics...
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Metrics Dashboard</h1>
          <p className="text-muted-foreground">
            System-wide performance and usage metrics
          </p>
        </div>
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Failed to load metrics. Please try again.
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Metrics Dashboard</h1>
        <p className="text-muted-foreground">
          System-wide performance and usage metrics
        </p>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              title="Total Devices"
              value={data?.totalDevices?.toString() || '0'}
              change="Real-time data"
              trend="up"
              icon={<Zap className="h-4 w-4 text-muted-foreground" />}
            />
            <MetricCard
              title="Devices Online (24h)"
              value={data?.devicesOnline24h?.toString() || '0'}
              change="Last 24 hours"
              trend="up"
              icon={<Activity className="h-4 w-4 text-muted-foreground" />}
            />
            <MetricCard
              title="Active Alerts"
              value={data?.activeAlerts?.toString() || '0'}
              change="Current count"
              trend={data && data.activeAlerts > 0 ? 'down' : 'up'}
              icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
            />
            <MetricCard
              title="Total Accounts"
              value={data?.accountCount?.toString() || '0'}
              change="Account count"
              trend="up"
              icon={<Users className="h-4 w-4 text-muted-foreground" />}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>System Overview</CardTitle>
                <CardDescription>Real-time statistics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Total Devices</span>
                    <span className="text-sm font-medium">{data?.totalDevices || 0}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Devices Online (24h)</span>
                    <span className="text-sm font-medium">{data?.devicesOnline24h || 0}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Active Alerts</span>
                    <span className="text-sm font-medium">{data?.activeAlerts || 0}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Total Accounts</span>
                    <span className="text-sm font-medium">{data?.accountCount || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Alerts</CardTitle>
                <CardDescription>Latest triggered alerts</CardDescription>
              </CardHeader>
              <CardContent>
                {data?.recentAlerts && data.recentAlerts.length > 0 ? (
                  <div className="space-y-3">
                    {data.recentAlerts.slice(0, 4).map((alert) => (
                      <div key={alert.id} className="flex items-start justify-between text-sm">
                        <div className="flex-1">
                          <p className="font-medium">{alert.deviceName || alert.deviceSerialNumber}</p>
                          <p className="text-xs text-muted-foreground">{alert.alertMessage}</p>
                        </div>
                        <Badge variant={alert.severityLevel === 'critical' ? 'destructive' : 'default'} className="ml-2">
                          {alert.severityLevel}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No recent alerts</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="devices" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Device Metrics</CardTitle>
              <CardDescription>Detailed device performance and usage statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Device metrics visualization will be implemented with actual data from the API.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Metrics</CardTitle>
              <CardDescription>User activity and engagement statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                User metrics visualization will be implemented with actual data from the API.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Alert Metrics</CardTitle>
              <CardDescription>Alert frequency and response statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Alert metrics visualization will be implemented with actual data from the API.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
